/**
 * Tiny rAF-driven tween helper (~30 LOC). Pixi v8 dropped its built-in
 * tweener, and bringing in gsap would add ~30 KB for an effect this small.
 */

export type Easing = (t: number) => number;

export const Easings = {
  linear: (t: number) => t,
  easeOutCubic: (t: number) => 1 - Math.pow(1 - t, 3),
  easeOutQuad: (t: number) => 1 - (1 - t) * (1 - t),
} as const;

export interface TweenOptions {
  durationMs: number;
  easing?: Easing;
  onUpdate(t: number): void;
  onComplete?(): void;
}

export interface TweenHandle {
  cancel(): void;
  done: Promise<void>;
}

export function tween(opts: TweenOptions): TweenHandle {
  const easing = opts.easing ?? Easings.easeOutCubic;
  let raf = 0;
  let cancelled = false;
  let resolveDone!: () => void;
  const done = new Promise<void>((r) => (resolveDone = r));
  const start = performance.now();

  const step = (now: number): void => {
    if (cancelled) return;
    const elapsed = now - start;
    const t = Math.min(1, elapsed / opts.durationMs);
    opts.onUpdate(easing(t));
    if (t < 1) {
      raf = requestAnimationFrame(step);
    } else {
      opts.onComplete?.();
      resolveDone();
    }
  };
  raf = requestAnimationFrame(step);

  return {
    cancel(): void {
      cancelled = true;
      cancelAnimationFrame(raf);
      resolveDone();
    },
    done,
  };
}
